import { gql, useApolloClient } from "@apollo/client";
import { useAsyncExecutor } from "async-executor-hook";
import { useState } from "react";
import styled from "styled-components";

import { ProcessingModal } from "../../common/components/ProcessingModal";
import DownArrow from "../../images/down_arrow.svg";
import DownloadDetail from "../../images/download_detail.svg";
import IconArrowDown from "../../images/icon_arrow_down.svg";
import IconArrowRight from "../../images/icon_arrow_right.svg";
import IconArrowUp from "../../images/icon_arrow_up.png";
import IconPdf from "../../images/image_pdf.svg";
import IconXlsx from "../../images/image_xlsx.svg";
import {
  CLIENT_TYPES,
  COMPLAINT_DETAIL_STEPS,
  COMPLAINT_PROCESS_INFO,
  COMPLAINT_RESULT,
  STATUS,
} from "../../utils/complaintsConstants";
import { GRAPHQL_METHODS } from "../../utils/constants";
import { dateToRegularFormat } from "../../utils/dates";
import { networkGraphqlRequest } from "../../utils/networkUtils";
import AdminComplaintsDetailClient from "../AdminComplaintsDetailClient/AdminComplaintsDetailClient";
import AdminComplaintsDetailComplaint from "../AdminComplaintsDetailComplaint/AdminComplaintsDetailComplaint";
import AdminComplaintsDetailResult from "../AdminComplaintsDetailResult/AdminComplaintsDetailResult";
import AdminComplaintsProcessInfo from "../AdminComplaintsProcessInfo/AdminComplaintsProcessInfo";
import Modal from "../Modal/Modal";
import Sidebar from "../Sidebar/Sidebar";
import StatusTag from "../StatusTag/StatusTag";
import StatusTooltip from "../StatusTooltip/StatusTooltip";
import {
  Container,
  GrayDiv,
  StatusSection,
  StepContent,
  StepDescription,
  StepNumber,
  StepsBar,
} from "./AdminComplaintsDetailSidebarStyles";
import requestData from "./requestData";

function AdminComplaintsDetailSidebar(props) {
  const client = useApolloClient();
  const { hideSidebar, complaint, refreshPage } = props;

  const [currentStep, updateCurrentStep] = useState(
    COMPLAINT_DETAIL_STEPS.CLIENT_INFO
  );
  const [complaintStatus, updateComplaintStatus] = useState(complaint.status);
  const [showStatusOptions, updateShowStatusOptions] = useState(false);

  const complaintStepList = [
    COMPLAINT_DETAIL_STEPS.CLIENT_INFO,
    COMPLAINT_DETAIL_STEPS.COMPLAINT_INFO,
    COMPLAINT_DETAIL_STEPS.PROCESS_INFO,
    COMPLAINT_DETAIL_STEPS.RESULT_INFO,
  ];

  const complaintStatusList = STATUS.filter(
    (status) => status.value !== ""
  ).map((status) => {
    return {
      ...status,
      callback: (e) => {
        e.stopPropagation();
        updateComplaintStatus(status);
        requestData(client, complaint.complaintId, status.value).then(() => {
          refreshPage();
        });
        updateShowStatusOptions(false);
      },
    };
  });

  const [processInfo, updateProcessInfo] = useState(
    complaint.processInformation
      ? setProcessFormat(complaint.processInformation)
      : COMPLAINT_PROCESS_INFO
  );

  const [result, updateResult] = useState(
    complaint.resultInformation
      ? setResultFormat(complaint.resultInformation)
      : COMPLAINT_RESULT
  );

  const clientHook = useState(
    processInfo.clientType
      ? CLIENT_TYPES.map((clientType) => {
          const currentClient = processInfo.clientType.filter((info) => {
            return info.client === clientType.value;
          })[0];
          return {
            inputValue: currentClient.clientName,
            checked: Boolean(currentClient.clientName),
            label: clientType.label,
            value: clientType.value,
          };
        })
      : CLIENT_TYPES.map((clientType) => {
          return {
            inputValue: "",
            checked: false,
            label: clientType.label,
            value: clientType.value,
          };
        })
  );

  const onProcessInfoChange = (updateInfo) => {
    updateProcessInfo({ ...processInfo, ...updateInfo });
  };

  const onResultChange = (updateInfo) => {
    updateResult({ ...result, ...updateInfo });
  };

  const onClickExpand = () => {
    updateShowStatusOptions(!showStatusOptions);
  };

  const [showDownloadDialog, updateShowDownloadDialog] = useState(false);

  return (
    <Sidebar onOutsideClick={hideSidebar}>
      {showDownloadDialog && (
        <ModalQuejasReclamos
          complaintId={complaint.complaintId}
          handleClose={() => {
            updateShowDownloadDialog(false);
          }}
        />
      )}

      <Container>
        <div className="detail-header">
          <div style={{ display: "flex" }}>
            <div style={{ width: "70%" }}>
              <div className="complaint-id">
                {complaint.demandIdentityByUser}
              </div>
              <div className="complaint-client">{complaint.client.name}</div>
              <GrayDiv>
                {dateToRegularFormat(
                  `${complaint.enterDate.split("T")[0]} 00:00`
                )}
              </GrayDiv>
            </div>
            <div style={{ width: "30%" }}>
              <MainButtonReclamos
                onClick={() => {
                  updateShowDownloadDialog(true);
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: 12 }}>Descargar detalle</div>
                  <img src={DownloadDetail} alt="downloadDetail" />
                </div>
              </MainButtonReclamos>
            </div>
          </div>
        </div>
        <StatusSection id="status-section">
          <div className="status-tag">
            <StatusTag status={complaintStatus} />
          </div>
          <div className="expand-status" onClick={onClickExpand}>
            <img
              src={showStatusOptions ? IconArrowUp : IconArrowDown}
              alt="arrow-down"
            />
          </div>
          {showStatusOptions && (
            <StatusTooltip
              className="tooltip"
              statusList={complaintStatusList}
            />
          )}
        </StatusSection>
        <StepsBar>
          <div className="step-tabs">
            {complaintStepList.map((step, index) => {
              const isCurrent = step === currentStep;
              return (
                <StepContent key={index} className="step-name">
                  <StepNumber
                    activated={isCurrent}
                    onClick={() => {
                      updateCurrentStep(step);
                    }}
                  >
                    {index + 1}
                  </StepNumber>
                  <div
                    onClick={() => {
                      updateCurrentStep(step);
                    }}
                    onKeyDown={() => {}}
                    role="button"
                  >
                    <StepDescription isCurrent={isCurrent}>
                      {step.label}
                    </StepDescription>
                  </div>

                  <div className="arrowIcon">
                    {index === complaintStepList.length - 1 ? null : (
                      <img src={IconArrowRight} alt="arrowRight"></img>
                    )}
                  </div>
                </StepContent>
              );
            })}
          </div>
        </StepsBar>
        {(() => {
          switch (currentStep) {
            case COMPLAINT_DETAIL_STEPS.CLIENT_INFO:
              return <AdminComplaintsDetailClient client={complaint.client} />;
            case COMPLAINT_DETAIL_STEPS.COMPLAINT_INFO:
              return <AdminComplaintsDetailComplaint complaint={complaint} />;
            case COMPLAINT_DETAIL_STEPS.PROCESS_INFO:
              return (
                <AdminComplaintsProcessInfo
                  processInfo={processInfo}
                  onProcessInfoChange={onProcessInfoChange}
                  complaintId={complaint.complaintId}
                  refreshPage={refreshPage}
                  clientHook={clientHook}
                />
              );
            case COMPLAINT_DETAIL_STEPS.RESULT_INFO:
              return (
                <AdminComplaintsDetailResult
                  result={result}
                  files={complaint.files}
                  onResultChange={onResultChange}
                  complaintId={complaint.complaintId}
                  refreshPage={refreshPage}
                />
              );
          }
        })()}
      </Container>
    </Sidebar>
  );
}

export default AdminComplaintsDetailSidebar;

function setProcessFormat(process) {
  return {
    ...process,
    videoRequestDate: process.videoRequestDate
      ? new Date(`${process.videoRequestDate.split("T")[0]} 00:00`)
      : null,
    videoUploadDate: process.videoUploadDate
      ? new Date(`${process.videoUploadDate.split("T")[0]} 00:00`)
      : null,
    incidenceDate: process.incidenceDate
      ? new Date(`${process.incidenceDate.split("T")[0]} 00:00`)
      : null,
    traceabilityRequestDate: process.traceabilityRequestDate
      ? new Date(`${process.traceabilityRequestDate.split("T")[0]} 00:00`)
      : null,
    traceabilityDeliveryDate: process.traceabilityDeliveryDate
      ? new Date(`${process.traceabilityDeliveryDate.split("T")[0]} 00:00`)
      : null,
    responseDate: process.responseDate
      ? new Date(`${process.responseDate.split("T")[0]} 00:00`)
      : null,
  };
}

function setResultFormat(result) {
  const { lastAnswer } = result;
  return {
    compensation: result.compensation ? result.compensation : "",
    compensationProcess: result.compensationProcess
      ? result.compensationProcess
      : "",
    responsibleForPayment: result.responsibleForPayment,
    compensationFile: result.compensationFile,
    socialReason: result.socialReason,
    bankAccount: result.bankAccount,
    bank: result.bank,
    transactionDocument: result.transactionDocument,
    caseNumber: result.caseNumber,
    approvalDate: lastAnswer.approvalDate
      ? new Date(`${lastAnswer.approvalDate.split("T")[0]} 00:00`)
      : null,
    businessDays: lastAnswer.businessDays,
    responseMedium: lastAnswer.responseMedium,
    observation: lastAnswer.observation,
    responseDays: lastAnswer.responseDays ? lastAnswer.responseDays : "",
    pending: lastAnswer.pending ? lastAnswer.pending : "",
    registerDate: lastAnswer.registerDate,
    paymentDate: result.paymentDate
      ? new Date(`${result.paymentDate.split("T")[0]} 00:00`)
      : null,
  };
}

export const ModalQuejasReclamos = (props) => {
  const complaintId = props.complaintId;

  const client = useApolloClient();

  const GET_PDF = gql`
    query ($caseNumber: String!) {
      getAdminReportToPDF(caseNumber: $caseNumber)
    }
  `;

  const GET_EXCEL = gql`
    query ($caseNumber: String!) {
      getAdminReportToExcel(caseNumber: $caseNumber)
    }
  `;

  const downloadFile = useAsyncExecutor(
    async (params: any) => {
      const query = params.type === "pdf" ? GET_PDF : GET_EXCEL;
      const resp = await networkGraphqlRequest(
        GRAPHQL_METHODS.QUERY,
        client,
        query,
        {
          caseNumber: params.complaintId,
        }
      );
      return resp[`getAdminReportTo${params.type === "pdf" ? "PDF" : "Excel"}`];
    },
    {
      onSuccess: (urlPdf) => {
        window.open(urlPdf);
      },
    }
  );

  return (
    <Modal onOutsideClick={() => props.handleClose()}>
      <ProcessingModal status={[downloadFile.status]}></ProcessingModal>
      <ContainerModal>
        <div>
          <div className="select-format-download">
            <b>Seleccione los formatos a descargar</b>
          </div>
          <div
            className="content-detail"
            style={{ cursor: "pointer" }}
            onClick={() => {
              downloadFile.execute({ complaintId, type: "pdf" });
            }}
          >
            <div style={{ display: "flex" }}>
              <div style={{ marginLeft: 15 }}>
                <img src={IconPdf} alt="pdf" />
              </div>
              <div className="detail-file">Detalle de la queja.pdf</div>
            </div>
            <div style={{ width: 30 }}>
              <div style={{ left: 0 }}>
                <img
                  src={DownArrow}
                  alt="downArrow"
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>
          </div>
          <div
            className="content-detail"
            style={{ cursor: "pointer" }}
            onClick={() => {
              downloadFile.execute({ complaintId, type: "excel" });
            }}
          >
            <div style={{ display: "flex" }}>
              <div style={{ marginLeft: 15 }}>
                <img src={IconXlsx} alt="" />
              </div>
              <div className="detail-file">Detalle de la queja.xlsx</div>
            </div>
            <div style={{ width: 30 }}>
              <div style={{ left: 0 }}>
                <img
                  src={DownArrow}
                  alt="downArrow"
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>
          </div>
        </div>
        <MainButtonReclamos
          onClick={() => {
            props.handleClose();
          }}
        >
          Listo
        </MainButtonReclamos>
      </ContainerModal>
    </Modal>
  );
};

export const MainButtonReclamos = styled.button`
  ${(props) => `
    background-color: ${props.theme.colors.primaryBlue};
    font-family: ${props.theme.fontFamilies.primaryFont};
  `}
  align-items: center;
  border: none;
  border-radius: 8px;
  color: ${(props) => props.theme.colors.white};
  cursor: pointer;
  display: flex;
  font-size: 1.5rem;
  font-weight: 700;
  height: 5rem;
  justify-content: center;
  margin-top: 3.2rem;
  width: 25.6rem;
`;

export const ContainerModal = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  align-items: center;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 4rem 2.5rem;
  width: 42.5rem;

  h1 {
    ${(props) => `
      color: ${props.theme.colors.blackTextTitle};
      font-family: ${props.theme.fontFamilies.primaryFont};
    `}
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 0;
  }

  p {
    ${(props) => `
      color: ${
        props.theme.colors.blackTextTitle + props.theme.transparencies.p58
      };
      font-family: ${props.theme.fontFamilies.primaryFont};
    `}
    font-size: 1.6rem;
    font-weight: 500;
    line-height: 2.4rem;
    margin-top: 2rem;
    padding: 0rem 1rem;
    text-align: center;
  }
  .select-format-download {
    font-family: Montserrat;
    font-size: 2.1rem;
    margin-bottom: 2.1rem;
    text-align: center;
  }
  .content-detail {
    align-items: center;
    background: #ffffff;
    border-radius: 1.1rem;
    box-shadow: 0px 5px 20px rgba(231, 231, 234, 0.6);
    display: flex;
    height: 5.2rem;
    justify-content: space-between;
    margin-top: 1.68rem;
  }
  .detail-file {
    align-items: center;
    color: #2e353d;
    display: flex;
    font-family: Montserrat;
    font-size: 1.4rem;
    margin-left: 1.5rem;
  }
`;
